import React, { useEffect, useState } from "react";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableContainer,
  TableHead,
  IconButton,
  Typography,
  Collapse,
  Button,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  ArrowDropDown,
  ArrowDropUp,
  Print,
  Visibility,
  Warning,
  Delete,
  Restore,
} from "@mui/icons-material";
import { Helper } from "../../utils/Helpers";
import { useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import { selectAuthData } from "../../core/store/redux/slices/Auth-slice";
import { useTranslation } from "react-i18next";
import { EditableCell } from "./EditPaymentHistory";
import { errorToster, successToster } from "../../shared/toster/Toster";
import { useLocation } from "react-router-dom";
const {
  newPrintPaymentReceipt,
  viewPaymentReceipt,
  getDaysDifferenceByStayType,
  roundOff,
  truncateNumberByDecimal,
  getNetRetFromBasePrice,
  getLocalTimeFromUtc,
  getGradientTextStyle,
} = new Helper();

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    background: theme.palette.tab.gradient,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    [theme.breakpoints.up("md")]: {
      fontSize: "0.83vw",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "1.44vw",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "3.08vw",
      padding: "2vw",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    [theme.breakpoints.up("md")]: {
      fontSize: "0.83vw",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "1.44vw",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "3.08vw",
    },
    fontWeight: 400,
  },
}));

export const CollapsedTableCell = styled(TableCell)(({ theme, bodyWidth }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontWeight: 500,
    paddingBottom: 0,
    border: 0,
    [theme.breakpoints.up("md")]: {
      fontSize: "0.83vw",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "1.44vw",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "3.08vw",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    color: "#000000",
    border: 0,
    [theme.breakpoints.up("md")]: {
      fontSize: "0.83vw",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "1.44vw",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "3.08vw",
    },
    fontWeight: 400,
  },
}));

const PaymentHistory = ({ bookingData, actions }) => {
  const { name, booking_id, data } = bookingData || {};
  const { t } = useTranslation();
  const { api } = ApiClient();
  const { palette } = useTheme();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentDetail, setPaymentDetail] = useState({});
  const [openRows, setOpenRows] = useState({});
  const [updatedData, setUpdatedData] = useState({});
  const { hotel } = useSelector(selectAuthData);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPaymentHistory, setEditedPaymentHistory] = useState([]);
  const [expectedTotal, setExpectedTotal] = useState(0);
  const [amountError, setAmountError] = useState(false);
  const [hotelConfig, setHotelConfig] = useState({});
  const [amountValidation, setAmountValidation] = useState({ min: 0, max: 0 });
  const [leastBookedOn, setLeastBookedOn] = useState(null);
  const userData = { ...hotel, ...hotelConfig };
  const isNotMob = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const location = useLocation();

  const setInitialData = async (bookingId) => {
    try {
      const [paymentHistory, paymentDetail, bookedRooms, hotelConfig] =
        await Promise.all([
          api
            .get(`${END_POINT.PAYMENT.GET_PAYMENT_HISTORY}${bookingId}`)
            .then((res) => res?.data?.data || null)
            .catch(() => null),
          api
            .get(
              `${END_POINT.PAYMENT.GET_PAYMENT_DETAIL_BY_BOOKING}${bookingId}`
            )
            .then((res) => res?.data?.data || null)
            .catch(() => null),
          api
            .post(
              `${END_POINT.ROOM.GET_BOOKED_ROOMS_BY_FILTER}`,
              { booking_id: bookingId },
              ""
            )
            .then((res) => res?.data?.data || [])
            .catch(() => []),
          api
            .get(END_POINT.HOTEL.GET_HOTEL_CONGIG)
            .then((res) => res?.data?.data || {})
            .catch(() => {}),
        ]);
      const payments = paymentHistory?.map((p) => ({
        ...p,
        // payment_date: getLocalTimeFromUtc(p.payment_date)?.dateTimeString,
      }));
      setPaymentHistory(payments || []);
      setPaymentDetail(paymentDetail);
      setUpdatedData({
        ...data,
        booked_rooms: bookedRooms,
      });
      setHotelConfig(hotelConfig);
      setAmountValidation({
        min: Math.round(paymentDetail.total_invoice_amount),
        max: Math.round(paymentDetail.total_amount),
      });
      setExpectedTotal(paymentDetail.total_paid_amount);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const guestBookingData = async (bookingId) => {
    try {
      let message = "error getting guesthistory by filter";
      let requestBody = {
        occupancy_status_code: "check_in",
        is_deleted: false,
        booking_id: bookingId,
      };
      const response = await api.post(
        END_POINT.GUESTINFO.GET_ALL_GUEST_HISTORY,
        requestBody,
        message
      );

      if (response.status === 200) {
        let booked_rooms = response.data.data[0].booked_rooms;
        const minBookedOn = booked_rooms.reduce((min, item) =>
          new Date(item.booked_on) < new Date(min.booked_on) ? item : min
        );
        let temp = getLocalTimeFromUtc(minBookedOn.booked_on).dateString;
        // let min = minBookedOn.booked_on.split(" ")[0].toString();
        setLeastBookedOn(temp);
      }
    } catch (error) {
      console.error("Error fetching data : ", error);
    }
  };

  useEffect(() => {
    if (!!booking_id) {
      setInitialData(booking_id);
      guestBookingData(booking_id);
    }
  }, [booking_id]);

  useEffect(() => {}, [editedPaymentHistory]);

  useEffect(() => {
    if (paymentHistory && paymentHistory.length > 0) {
      setEditedPaymentHistory(
        paymentHistory.map((item) => ({
          ...item,
          delete: false,
        }))
      );
      if (!isNotMob) {
        setOpenRows(
          paymentHistory.reduce((acc, _, index) => {
            acc[index] = true;
            return acc;
          }, {})
        );
      }
    }
  }, [paymentHistory, isNotMob]);
  useEffect(() => {
    if (isEditing && editedPaymentHistory.length > 0) {
      // let dateVal = editedPaymentHistory.find((item) => {
      //   const itemDate = new Date(item.payment_date)
      //     .toISOString()
      //     .split("T")[0];
      //   const minDate = new Date(leastBookedOn).toISOString().split("T")[0];
      //   const todayDate = new Date().toISOString().split("T")[0];

      //   return minDate > itemDate || todayDate < itemDate;
      // });
      // if (dateVal) setDateError(true);
      // else setDateError(false);
      let is_zero = editedPaymentHistory.find((item) => item.paid_amount <= 0);
      let total = editedPaymentHistory
        .filter((item) => !item.delete)
        .reduce((sum, item) => Number(sum) + Number(item.paid_amount), 0);

      if (
        total < amountValidation.min ||
        total > amountValidation.max ||
        is_zero
      ) {
        setAmountError(true);
      } else {
        setAmountError(false);
      }
    }
  }, [editedPaymentHistory, expectedTotal, isEditing]);

  const toggleRow = (index) => {
    setOpenRows((prevOpenRows) => ({
      ...prevOpenRows,
      [index]: !prevOpenRows[index],
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedPaymentHistory(
      paymentHistory.map((item) => ({
        ...item,
        delete: false,
      }))
    );
  };

  const handleSave = async () => {
    if (amountError) {
      return;
    }

    let totalAmountPaid = editedPaymentHistory
      .filter((item) => !item.delete)
      .reduce((sum, item) => sum + item.paid_amount, 0);

    if (totalAmountPaid < amountValidation.min) {
      setIsEditing(false);
      errorToster("Total Amount Paid lesser than invoice amount");
      return;
    }

    try {
      // Group all payment updates and deletions into a single payload
      const payload = {
        paymentsToUpdate: editedPaymentHistory.filter(
          (payment) => !payment.delete
        ),
        paymentsToDelete: editedPaymentHistory
          .filter((payment) => payment.delete)
          .map((payment) => payment.payment_id),
      };

      // Make a single API call
      await api.post(
        `${END_POINT.PAYMENT.BATCH_UPDATE_PAYMENTS}`,
        payload,
        null,
        "Failed to update payments"
      );

      setInitialData(booking_id);
      successToster("Payment updated successfully!");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating payments:", error);
      errorToster("Unable to edit payment");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedPaymentHistory([...paymentHistory]);
    setAmountError(false);
  };

  const handleChange = (index, field, value) => {
    // if(field == "paid_amount" && value == 0){
    //   setAmountError(true)
    // }
    const updatedHistory = [...editedPaymentHistory];
    updatedHistory[index] = {
      ...updatedHistory[index],
      [field]: value,
    };

    setEditedPaymentHistory(updatedHistory);
  };

  const handleReceipt = (data, payment, receiptCallback) => {
    data.paid_amount = roundOff(Number(payment?.paid_amount));
    data.payment_type = payment?.payment_type;
    data.payment_date = payment?.payment_date;
    data.receipt_number = payment?.receipt_number;
    data?.booked_rooms?.forEach((room) => {
      const gstRate = Number(room?.total_tax_rate) / 100;
      const taxAmount = truncateNumberByDecimal(
        Number(room?.occupancy_price) * gstRate
      );
      const stayDays =
        room?.checkin_date &&
        room?.expected_checkout_date &&
        room?.stay_type_code
          ? getDaysDifferenceByStayType(
              getLocalTimeFromUtc(
                room.checkin_date?.replace("T", " ").split(".")[0]
              ).dateTimeString,
              getLocalTimeFromUtc(
                room.expected_checkout_date?.replace("T", " ").split(".")[0]
              ).dateTimeString,
              room.stay_type_code,
              room.checkin_grace_hour
            )
          : 0;
      const netRent = getNetRetFromBasePrice(
        Number(room?.occupancy_price),
        stayDays,
        Number(room?.total_tax_rate)
      );
      Object.assign(room, {
        sgst: room?.total_tax_rate / 2,
        cgst: room?.total_tax_rate / 2,
        taxAmount,
        netRent,
        stayDays,
      });
    });
    receiptCallback({
      billHeader: "ADVANCE RECEIPT",
      userData: userData,
      bookingData: data,
    });
  };

  return (
    <Box
      sx={{
        minHeight: { md: "19.44vw", xs: "33.58vw" },
        width: { xs: "91.795vw ", sm: "auto" },
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: "1% 0 3% 0",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "4.10vw", sm: "1.92vw", md: "1.12vw" },
              fontWeight: 600,
            }}
          >
            {t("common_components.payments.payment_history")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: "flex", sm: "flex" },
            alignItems: "center",
            justifyContent: "right",
            width: "50%",
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: "3.52vw",
                sm: "2.52vw",
                md: "1.12vw",
                ...getGradientTextStyle(palette),
              },
            }}
          >
            {name || "-"}
          </Typography>
        </Box>
      </Box>
      <TableContainer
        sx={{
          maxHeight: { md: "19.44vw", sm: "33.58vw" },
          width: { sm: "max-content" },
          borderRadius: "4px",
        }}
      >
        <Table stickyHeader>
          <TableHead sx={{ backgroundColor: "#D9A62E", paddingY: "3vw" }}>
            <TableRow sx={{ textWrap: "nowrap" }}>
              <StyledTableCell>
                {isNotMob && t("common_components.payments.serial_number")}
              </StyledTableCell>
              <StyledTableCell align="center">
                {t("common_components.payments.date")}
              </StyledTableCell>
              <StyledTableCell>
                {t("common_components.payments.amount_paid")}
              </StyledTableCell>
              {isNotMob && (
                <>
                  <StyledTableCell>
                    {t("common_components.payments.payment_mode")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {t("common_components.payments.remarks")}
                  </StyledTableCell>
                </>
              )}
              {true && (
                <StyledTableCell align="center">
                  {t("common_components.payments.action")}
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {paymentHistory?.length > 0 ? (
              (isEditing ? editedPaymentHistory : paymentHistory)?.map(
                (payment, ind) => (
                  <React.Fragment key={ind}>
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom: "border: 1px solid #FFE097 !important",
                          backgroundColor: "#FCF9F2",
                        },
                      }}
                    >
                      {!isNotMob ? (
                        <StyledTableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(ind)}
                          >
                            {openRows[ind] ? (
                              <ArrowDropUp
                                sx={{ color: "#D9A62E" }}
                                fontSize="small"
                              />
                            ) : (
                              <ArrowDropDown
                                sx={{ color: "#D9A62E" }}
                                fontSize="small"
                              />
                            )}
                          </IconButton>
                        </StyledTableCell>
                      ) : (
                        <StyledTableCell align="center">
                          {ind + 1}
                        </StyledTableCell>
                      )}
                      <StyledTableCell
                        align="center"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {isEditing
                          ? // <TextField
                            //   fullWidth
                            //   type="date-time"
                            //   size="small"
                            //   disabled={
                            //     editedPaymentHistory[ind]?.delete ?? false
                            //   }
                            //   value={
                            //     payment?.payment_date
                            //       ? payment.payment_date
                            //       : ""
                            //   }
                            //   onChange={(event) =>
                            //     handleChange(
                            //       ind,
                            //       "payment_date",
                            //       event.target.value
                            //     )
                            //   }
                            //   InputLabelProps={{ shrink: true }}
                            //   inputProps={{
                            //     min: leastBookedOn,
                            //     max: new Date().toISOString().split("T")[0], // Restricts max date to today
                            //   }}
                            // />
                            getLocalTimeFromUtc(payment?.payment_date)
                              .dateString
                          : !!getLocalTimeFromUtc(payment?.payment_date)
                              .dateString
                          ? getLocalTimeFromUtc(payment?.payment_date)
                              .dateString
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell>
                        {isEditing ? (
                          <EditableCell
                            isEditing={true}
                            isDisabled={
                              editedPaymentHistory[ind].delete ? true : false
                            }
                            value={Number(payment?.paid_amount) || ""}
                            onChange={(value) =>
                              handleChange(ind, "paid_amount", value)
                            }
                            field="paid_amount"
                            type="number"
                            rowIndex={ind}
                          />
                        ) : (
                          `₹ ${roundOff(payment?.paid_amount)}`
                        )}
                      </StyledTableCell>
                      {isNotMob && (
                        <>
                          <StyledTableCell>
                            {isEditing ? (
                              <EditableCell
                                isEditing={true}
                                isDisabled={
                                  editedPaymentHistory[ind].delete
                                    ? true
                                    : false
                                }
                                value={payment?.payment_type || ""}
                                onChange={(value) =>
                                  handleChange(ind, "payment_type", value)
                                }
                                field="payment_type"
                                type="select"
                                rowIndex={ind}
                                options={[
                                  {
                                    payment_type_id: "Cash",
                                    payment_type: "Cash",
                                  },
                                  {
                                    payment_type_id: "Card",
                                    payment_type: "Card",
                                  },
                                  {
                                    payment_type_id: "UPI",
                                    payment_type: "UPI",
                                  },
                                  {
                                    payment_type_id: "NEFT",
                                    payment_type: "NEFT",
                                  },
                                  {
                                    payment_type_id: "Paid via OTA",
                                    payment_type: "Paid via OTA",
                                  },
                                ]}
                              />
                            ) : payment?.payment_type ? (
                              t(
                                `payment_mode_dropdown.${payment.payment_type
                                  ?.toLowerCase()
                                  .replace(/\s+/g, "_")}`
                              )
                            ) : (
                              "-"
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {isEditing ? (
                              <EditableCell
                                isEditing={true}
                                isDisabled={
                                  editedPaymentHistory[ind].delete
                                    ? true
                                    : false
                                }
                                value={payment?.remarks || ""}
                                onChange={(value) =>
                                  handleChange(ind, "remarks", value)
                                }
                                field="remarks"
                                type="text"
                                rowIndex={ind}
                              />
                            ) : (
                              payment?.remarks || "-"
                            )}
                          </StyledTableCell>
                        </>
                      )}
                      {actions && !isEditing ? (
                        <StyledTableCell align="center">
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton
                              onClick={() =>
                                handleReceipt(
                                  updatedData,
                                  payment,
                                  viewPaymentReceipt
                                )
                              }
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                handleReceipt(
                                  updatedData,
                                  payment,
                                  newPrintPaymentReceipt
                                )
                              }
                              size="small"
                            >
                              <Print fontSize="small" />
                            </IconButton>
                          </Box>
                        </StyledTableCell>
                      ) : (
                        <StyledTableCell align="center">
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {editedPaymentHistory[ind]?.delete === true ? (
                              <IconButton
                                onClick={() => {
                                  let tempHistory = [...editedPaymentHistory];
                                  tempHistory[ind].delete = false;
                                  setEditedPaymentHistory([...tempHistory]);
                                }}
                                size="small"
                              >
                                <Restore fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                // disabled={amountError}
                                onClick={() => {
                                  let tempHistory = [...editedPaymentHistory];
                                  tempHistory[ind].delete = true;
                                  setEditedPaymentHistory([...tempHistory]);
                                }}
                                size="small"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </StyledTableCell>
                      )}
                    </TableRow>
                    <TableRow
                      sx={{
                        borderBottom: openRows[ind]
                          ? "none"
                          : "1px solid #FFEABA",
                        backgroundColor: "#FCF9F2",
                        display: isNotMob ? "none" : "table-row",
                      }}
                    >
                      <TableCell
                        style={{
                          paddingBottom: 0,
                          paddingTop: 0,
                          borderColor: "#FFEABA",
                        }}
                        colSpan={9}
                      >
                        <Collapse
                          in={openRows[ind]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <CollapsedTableCell>
                                    Payment Mode
                                  </CollapsedTableCell>
                                  <CollapsedTableCell>
                                    Remarks
                                  </CollapsedTableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <CollapsedTableCell sx={{ color: "#DEA829" }}>
                                    {isEditing ? (
                                      <EditableCell
                                        isEditing={true}
                                        isDisabled={
                                          editedPaymentHistory[ind].delete
                                            ? true
                                            : false
                                        }
                                        value={payment?.payment_type || ""}
                                        onChange={(value) =>
                                          handleChange(ind, "payment_type", value)
                                        }
                                        field="payment_type"
                                        type="select"
                                        rowIndex={ind}
                                        options={[
                                          {
                                            payment_type_id: "Cash",
                                            payment_type: "Cash",
                                          },
                                          {
                                            payment_type_id: "Card",
                                            payment_type: "Card",
                                          },
                                          {
                                            payment_type_id: "UPI",
                                            payment_type: "UPI",
                                          },
                                          {
                                            payment_type_id: "NEFT",
                                            payment_type: "NEFT",
                                          },
                                          {
                                            payment_type_id: "Paid via OTA",
                                            payment_type: "Paid via OTA",
                                          },
                                        ]}
                                      />
                                    ) : (
                                      payment?.payment_type
                                    )}
                                  </CollapsedTableCell>
                                  <CollapsedTableCell sx={{ color: "#DEA829" }}>
                                    {isEditing ? (
                                      <EditableCell
                                        isEditing={true}
                                        isDisabled={
                                          editedPaymentHistory[ind].delete
                                            ? true
                                            : false
                                        }
                                        value={payment?.remarks || ""}
                                        onChange={(value) =>
                                          handleChange(ind, "remarks", value)
                                        }
                                        field="remarks"
                                        type="text"
                                        rowIndex={ind}
                                      />
                                    ) : (
                                      payment?.remarks || "-"
                                    )}
                                  </CollapsedTableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              )
            ) : (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ padding: "2%", color: "#bab9ba" }}
                >
                  {t("pages.home_page.todays_activity.no_data_found")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          margin: { xs: "12px 0 0", sm: "2% 0" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        <Box>
          {isEditing && (
            <Typography
              sx={{
                fontSize: { xs: "3vw", sm: "1.6vw", md: "0.7vw" },
                color: "#DB563F",
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                gap: "4px",
              }}
            >
              {amountError && (
                <Box sx={{ display: "flex" }}>
                  <Warning fontSize="small" />
                  {`Total Amount Exceeds or Lesser than Total Invoice`}
                </Box>
              )}
              {/* {dateError && (
                <Box sx={{ display: "flex" }}>
                  <Warning fontSize="small" />
                  {`Date should be between booking date and today's date`}
                </Box>
              )} */}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "flex-end",
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1.5, sm: "20px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              position: { xs: "static", sm: "absolute" },
              bottom: { sm: "-10px" },
              right: { sm: "0" },
              left: { sm: "0" },
              display: "flex",
              justifyContent: "center",
              gap: { xs: "12px", sm: "20px" },
              backgroundColor: "transparent",
              width: { xs: "100%", sm: "auto" },
              mt: { xs: 2, sm: 0 },
            }}
          >
            {isEditing ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    fontSize: { xs: "3.1vw", sm: "1.1vw", md: "0.8vw" },
                    textTransform: "none",
                    minWidth: { xs: "28vw", sm: "auto" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={amountError}
                  sx={{
                    fontSize: { xs: "3.1vw", sm: "1.1vw", md: "0.8vw" },
                    textTransform: "none",
                    minWidth: { xs: "28vw", sm: "auto" },
                  }}
                >
                  Save
                </Button>
              </>
            ) : (
              !location.pathname.includes("departure") &&
              paymentHistory.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handleEdit}
                  sx={{
                    fontSize: { xs: "3.1vw", sm: "1.1vw", md: "0.8vw" },
                    textTransform: "none",
                    minWidth: { xs: "28vw", sm: "auto" },
                  }}
                >
                  Edit
                </Button>
              )
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "space-between", sm: "flex-end" },
              alignItems: "center",
              gap: { xs: 1, sm: "20px" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "3.59vw", sm: "1.68vw", md: "0.97vw" },
                color: "#DB563F",
                whiteSpace: "nowrap",
              }}
            >
              {`${t("common_components.payments.due")}: ₹ ${
              roundOff(paymentDetail?.total_booking_due || 0)
            }`}</Typography>
            {/* OLD CALCULATION (Commented Out):
            <Typography>
            {`${t("common_components.payments.due")}: ₹ ${
              paymentDetail?.due_amount || paymentDetail?.reservation_amount
                ? roundOff(paymentDetail?.due_amount) +
                  roundOff(paymentDetail?.reservation_amount || 0)
                : "N/A"
            }`}
            </Typography>
            */}
            <Typography
              sx={{
                fontSize: { xs: "3.59vw", sm: "1.68vw", md: "0.97vw" },
                fontWeight: 500,
                whiteSpace: "nowrap",
                textAlign: "right",
              }}
            >
              {`${t("common_components.payments.total")}: ₹ ${
              paymentDetail?.total_amount || paymentDetail?.reservation_amount
                ? roundOff(paymentDetail?.total_amount || 0) +
                  roundOff(paymentDetail?.reservation_amount || 0)
                : "N/A"
            }`}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentHistory;
